import { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import { supabase } from "../lib/supabase";
import Spinner from "./common/Spinner";
import { Badge } from "../components/common/Badge";
import { ConsultationModeBadge } from "./ConsultationMode";
import Button from "../components/common/Button";
import { Input } from "../components/common/Input";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { useNavigate } from "react-router-dom";
import * as utils from "../utils/utils";
import {
  Clock,
  User,
  Phone,
  Search,
  UserCheck,
  Calendar,
  Users,
  ArrowLeft,
  Stethoscope,
  X,
  AlertCircle,
} from "lucide-react";
import customToast from "./common/Toaster";
import PatientSummaryInfoDialog from "./PatientSummaryInfoDialog";
import useBrandTranslation from "../hooks/useBrandTranslation";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "./common/Dialog";
import { cancelAppointment as cancelAppointmentApi } from "../apis/api.appointments";
import {
  formatClockTime,
  formatDateDMY,
} from "../lib/ScheduleUtils";
import { getLocalizedDayName } from "../i18n/scheduleLabels";

export default function WaitingRoomSecretary() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const { appointments, profiles, loadData } = useData();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingAppointment, setUpdatingAppointment] = useState(null);
  const [cancellations, setCancellations] = useState([]);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Cargar cancelaciones
  const loadCancellations = async () => {
    try {
      const { data, error } = await supabase
        .from("appointment_cancellations")
        .select("*")
        .order("appointment_date", { ascending: false });

      if (error) throw error;
      setCancellations(
        (data || []).map((c) => ({
          ...c,
          appointment_start_time: formatClockTime(c.appointment_start_time),
          appointment_end_time: formatClockTime(c.appointment_end_time),
        }))
      );
    } catch (error) {
      console.error("Error loading cancellations:", error);
    }
  };

  // Actualizar datos cada 5 segundos para mantener actualizado el conteo de recepción
  useEffect(() => {
    loadCancellations();
    const interval = setInterval(() => {
      loadData();
      loadCancellations();
    }, 5000); // 5000 milisegundos = 5 segundos

    // Limpiar el intervalo cuando el componente se desmonte
    return () => clearInterval(interval);
  }, [loadData]);

  // Filtrar turnos del día actual (reservados + en recepción)
  const todayAppointments = useMemo(() => {
    if (!appointments) return [];

    const today = new Date().toISOString().split("T")[0];

    return appointments
      .filter(
        (apt) =>
          (apt.status === "booked" || apt.status === "present") &&
          apt.date === today
      )
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [appointments]);

  // Filtrar por búsqueda
  const filteredAppointments = useMemo(() => {
    if (!searchTerm) return todayAppointments;

    return todayAppointments.filter((apt) => {
      const patientName = apt.patient_name || "";
      const doctorProfile = profiles.find((p) => p.id === apt.doctor_id);
      const doctorName = doctorProfile
        ? `${doctorProfile.name} ${doctorProfile.last_name}`
        : "";

      return (
        patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatClockTime(apt.start_time).includes(searchTerm)
      );
    });
  }, [todayAppointments, searchTerm, profiles]);

  // Obtener información del paciente
  const getPatientInfo = (appointment) => {
    if (appointment.patient_name) {
      return {
        name: appointment.patient_name,
        phone: appointment.patient_phone || "No disponible",
        email: "No disponible",
      };
    }

    const patient = profiles.find((p) => p.id === appointment.patient_id);
    return {
      name: patient ? `${patient.name} ${patient.last_name}` : "Paciente",
      phone: patient?.phone || "No disponible",
      email: patient?.email || "No disponible",
    };
  };

  const getPatientProfileForDialog = (appointment) => {
    if (appointment.patient_id) {
      const profile = profiles.find((p) => p.id === appointment.patient_id);
      if (profile) return profile;
    }
    const nameParts = (appointment.patient_name || "").trim().split(/\s+/);
    return {
      name: nameParts[0] || appointment.patient_name || "Paciente",
      last_name: nameParts.slice(1).join(" "),
      patient_name: appointment.patient_name,
      emergency_contact_phone: appointment.patient_phone,
    };
  };

  // Obtener información del doctor
  const getDoctorInfo = (doctorId) => {
    const doctor = profiles.find((p) => p.id === doctorId);
    return doctor ? `Dr. ${doctor.name} ${doctor.last_name}` : "Doctor";
  };

  // Obtener cancelaciones de un paciente
  const getPatientCancellations = (appointment) => {
    if (!cancellations || cancellations.length === 0) return [];

    const patientInfo = getPatientInfo(appointment);
    const patientId = appointment.patient_id;
    const patientName = patientInfo.name;

    // Filtrar cancelaciones por patient_id o patient_name
    return cancellations.filter((cancellation) => {
      if (patientId && cancellation.patient_id) {
        return cancellation.patient_id === patientId;
      }
      if (cancellation.patient_name && patientName) {
        return cancellation.patient_name.toLowerCase() === patientName.toLowerCase();
      }
      return false;
    });
  };

  // Marcar paciente como presente
  const markAsPresent = async (appointmentId) => {
    setUpdatingAppointment(appointmentId);
    try {
      const { error } = await supabase
        .from("appointments")
        .update({ status: "present" })
        .eq("id", appointmentId);

      if (error) throw error;

      // Recargar datos
      await loadData();
    } catch (error) {
      console.error("Error updating appointment:", error);
      customToast(t("app.appointments.updateError", "Error al actualizar el turno"), "danger");
    } finally {
      setUpdatingAppointment(null);
    }
  };

  // Cancelar turno
  const cancelAppointment = async (appointmentId) => {
    const appointment = filteredAppointments.find((apt) => apt.id === appointmentId);
    if (!appointment) return;
    setAppointmentToCancel(appointment);
  };

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel || !user) return;

    setIsCancelling(true);
    setUpdatingAppointment(appointmentToCancel.id);
    try {
      await cancelAppointmentApi({
        appointmentId: appointmentToCancel.id,
        cancelledBy: user.id,
      });

      await loadData();
      await loadCancellations();
      setAppointmentToCancel(null);
      customToast(
        t("app.appointments.cancelSuccess", "Turno cancelado correctamente"),
        "success"
      );
    } catch (error) {
      console.error("Error canceling appointment:", error);
      customToast(t("app.appointments.cancelError", "Error al cancelar el turno"), "danger");
    } finally {
      setIsCancelling(false);
      setUpdatingAppointment(null);
    }
  };

  if (!user) return <Spinner className="w-10 h-10 mx-auto" />;

  if (user && user.role !== "secretary") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.appointments.restrictedAccess", "Acceso Restringido")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              Esta sección es solo para secretarias.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="outline"
                onClick={() => utils.goBack()}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <UserCheck className="w-8 h-8 text-green-600" />
              {t("app.waitingRoom.title", "Recepción de Pacientes")}
            </h1>
            <p className="text-gray-600 mt-2">
              {t(
                "app.waitingRoom.subtitleSecretary",
                "Gestión de llegada de pacientes — {{name}} {{lastName}}",
                { name: user.name, lastName: user.last_name }
              )}
            </p>
            <p className="text-sm text-gray-500">
              {getLocalizedDayName(new Date().getDay())} {formatDateDMY(new Date())}
            </p>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {todayAppointments.length}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.waitingRoom.dailyAppointments", "Turnos del Día")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {
                      appointments.filter(
                        (apt) =>
                          apt.status === "present" &&
                          apt.date === new Date().toISOString().split("T")[0]
                      ).length
                    }
                  </p>
                  <p className="text-sm text-gray-600">{t("app.waitingRoom.inWaitingRoom", "En recepción")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-green-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {
                      todayAppointments.filter(
                        (apt) => apt.appointment_type === "private"
                      ).length
                    }
                  </p>
                  <p className="text-sm text-gray-600">{t("app.waitingRoom.private", "Particulares")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {todayAppointments.length > 0
                      ? formatClockTime(todayAppointments[0].start_time)
                      : "--:--"}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.waitingRoom.firstAppointment", "Primer Turno")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Búsqueda */}
        <Card className="mb-6">
          <CardContent className="!pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={t("app.waitingRoom.search", "Buscar por paciente, doctor o horario...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Lista de Turnos */}
        <Card className="mb-20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Turnos Programados para Hoy ({filteredAppointments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredAppointments.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  {searchTerm
                    ? "No se encontraron turnos"
                    : "No hay turnos programados para hoy"}
                </p>
                <p className="text-gray-400 text-sm">
                  {searchTerm
                    ? "Intenta con otros términos de búsqueda"
                    : "Los turnos aparecerán aquí cuando se reserven"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAppointments.map((appointment) => {
                  const patientInfo = getPatientInfo(appointment);
                  const doctorName = getDoctorInfo(appointment.doctor_id);
                  const isUpdating = updatingAppointment === appointment.id;

                  return (
                    <div
                      key={appointment.id}
                      className="border rounded-lg p-4 transition-all hover:shadow-md"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col items-center">
                            <Clock className="w-5 h-5 text-gray-500" />
                            <span className="text-sm font-medium">
                              {formatClockTime(appointment.start_time)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatClockTime(appointment.end_time)}
                            </span>
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <User className="w-4 h-4 text-gray-500" />
                              <span className="font-medium text-lg">
                                {patientInfo.name}
                              </span>
                            </div>

                            <div className="text-sm text-gray-600 mb-1">
                              <strong>{t("app.waitingRoom.doctor", "Doctor:")}</strong> {doctorName}
                            </div>

                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              {patientInfo.phone !== "No disponible" && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  <span>{patientInfo.phone}</span>
                                </div>
                              )}
                            </div>

                            {appointment.notes && (
                              <div className="mt-2 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                                <strong>{t("app.waitingRoom.reason", "Motivo:")}</strong> {appointment.notes}
                              </div>
                            )}

                            {/* Cancelaciones del paciente */}
                            {(() => {
                              const patientCancellations = getPatientCancellations(appointment);
                              if (patientCancellations.length > 0) {
                                return (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                      <AlertCircle className="w-4 h-4 text-orange-500" />
                                      <span className="text-xs font-semibold text-orange-600">
                                        Cancelaciones anteriores: {patientCancellations.length}
                                      </span>
                                    </div>
                                    <div className="space-y-1">
                                      {patientCancellations.slice(0, 3).map((cancellation) => (
                                        <div
                                          key={cancellation.id}
                                          className="text-xs text-gray-600 bg-orange-50 p-2 rounded"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span>
                                              {formatDateDMY(
                                                cancellation.appointment_date
                                              )}
                                            </span>
                                            <span className="font-medium">
                                              {formatClockTime(
                                                cancellation.appointment_start_time
                                              )}
                                            </span>
                                          </div>
                                          {cancellation.cancellation_reason && (
                                            <div className="text-gray-500 mt-1">
                                              {cancellation.cancellation_reason}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                      {patientCancellations.length > 3 && (
                                        <div className="text-xs text-gray-500 italic">
                                          +{patientCancellations.length - 3} cancelación(es) más
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                appointment.appointment_type === "private"
                                  ? "appointmentPrivate"
                                  : "appointmentInsurance"
                              }
                            >
                              {appointment.appointment_type === "private"
                                ? "Particular"
                                : "Obra Social"}
                            </Badge>
                            <ConsultationModeBadge
                              mode={appointment.consultation_mode}
                              className="text-xs"
                            />
                            {appointment.status === "present" && (
                              <Badge
                                variant="outline"
                                className="bg-amber-400 text-amber-950 border-amber-500"
                              >
                                {t("app.waitingRoom.inReceptionBadge", "En recepción")}
                              </Badge>
                            )}
                          </div>

                          {appointment.price && (
                            <span className="text-sm font-medium text-green-600">
                              ${appointment.price}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end items-center gap-2">
                        <PatientSummaryInfoDialog
                          patient={getPatientProfileForDialog(appointment)}
                          className="h-9 w-9 shrink-0 p-0"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelAppointment(appointment.id)}
                          disabled={isUpdating}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => markAsPresent(appointment.id)}
                          disabled={
                            isUpdating || appointment.status === "present"
                          }
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          {isUpdating
                            ? "..."
                            : appointment.status === "present"
                            ? t("app.waitingRoom.present", "Presente")
                            : t("app.waitingRoom.markPresent", "Marcar presente")}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={!!appointmentToCancel}
        onOpenChange={(open) => !open && setAppointmentToCancel(null)}
      >
        <DialogContent className="w-[95vw] max-w-md rounded-xl p-6">
          <DialogTitle className="text-lg font-bold text-gray-900 mb-4">
            {t(
              "app.appointments.confirmCancelGeneric",
              "¿Estás seguro que deseas cancelar?"
            )}
          </DialogTitle>
          <p className="text-gray-600 text-sm mb-6">
            {t(
              "app.appointments.confirmCancelDesc",
              "Esta acción liberará el horario para otros pacientes."
            )}
          </p>
          <div className="flex gap-3 justify-end">
            <DialogClose asChild>
              <Button variant="outline" disabled={isCancelling}>
                {t("common.cancel", "Cancelar")}
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={isCancelling}
            >
              {isCancelling
                ? "..."
                : t("app.appointments.confirmYes", "Sí, cancelar")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
